import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { progressApi } from '@/api/progress';
import { humanizeError } from '@/lib/errors';

/**
 * Поле оценки за урок на экране «Прогресс ученика». Разбаловка, 10.09.2026.
 *
 * ⚠️ ПУСТОЕ ПОЛЕ И НОЛЬ — РАЗНЫЕ СОСТОЯНИЯ. Пусто значит «не оценивали»,
 * ноль — «куратор посмотрел и поставил ноль». В шкале 0–100 ноль стал
 * настоящей оценкой, и слить эти состояния в одно значит соврать ученику:
 * он увидит одинаковую пустоту и там, где работу разобрали, и там, где до
 * неё не дошли. Поэтому очистка поля — это отдельное действие (снять оценку),
 * а не «поставить ноль».
 *
 * ⚠️ ПОЛЕ ВЫКЛЮЧЕНО У НЕПРОЙДЕННОГО УРОКА. Сервер такую оценку всё равно
 * отвергнет с 409 — решение владельца: очки не должны попадать в рейтинг без
 * прогресса. Интерфейс не должен предлагать того, что будет отвергнуто.
 *
 * Само поле показывается только в режиме `curator` — решает вызывающий.
 */
export function LessonGradeField({
  studentId,
  lessonId,
  grade,
  isCompleted,
}: {
  studentId: string;
  lessonId: string;
  grade?: number | null;
  isCompleted: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  /** Число из сервера в текст поля: null → пусто, 0 → «0». */
  const asText = (v?: number | null) => (v === null || v === undefined ? '' : String(v));

  const [value, setValue] = useState(asText(grade));
  const [error, setError] = useState('');

  // Список перечитывается после сохранения — подхватываем пришедшее значение,
  // иначе поле показывало бы то, что набрали, даже если сервер записал другое.
  useEffect(() => setValue(asText(grade)), [grade]);

  const save = useMutation({
    mutationFn: async (next: string) => {
      const trimmed = next.trim();
      if (trimmed === '') {
        await progressApi.clearLessonGrade(studentId, lessonId);
        return;
      }
      await progressApi.setLessonGrade(studentId, lessonId, Number(trimmed));
    },
    onSuccess: () => {
      setError('');
      qc.invalidateQueries({ queryKey: ['staff', 'student-lessons', studentId] });
    },
    onError: (e) => {
      setError(humanizeError(e));
      setValue(asText(grade));
    },
  });

  const commit = () => {
    const trimmed = value.trim();
    if (trimmed === asText(grade)) return; // ничего не изменилось

    // Проверяем ЗДЕСЬ же, а не только на сервере: отказ должен быть виден
    // сразу у поля, а не приходить кругом через сеть.
    if (trimmed !== '') {
      const n = Number(trimmed);
      if (!Number.isInteger(n) || n < 0 || n > 100) {
        setError(t('studentProgress.gradeRange'));
        setValue(asText(grade));
        return;
      }
    }
    save.mutate(trimmed);
  };

  return (
    <div className="flex flex-none flex-col items-end gap-0.5">
      <label className="flex items-center gap-1.5">
        <span className="text-xs text-on-surface-variant">{t('studentProgress.grade')}</span>
        <input
          type="number"
          min={0}
          max={100}
          inputMode="numeric"
          value={value}
          disabled={!isCompleted || save.isPending}
          placeholder={t('studentProgress.notGraded')}
          onChange={(e) => setValue(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          className="w-16 rounded-button border border-outline-variant bg-surface-container-low px-2 py-1 text-right text-sm text-on-surface disabled:opacity-40"
        />
      </label>
      {error && <span className="max-w-[12rem] text-right text-[11px] text-error">{error}</span>}
    </div>
  );
}
